import { type Api, ApiFailure, type MemberPatch, api as realApi } from '@/api/client'
import * as repo from '@/db/repo'
import { type OutboxEntry, PUSH_CHUNK, orderForPush, pendingKeys, toEntry } from '@/domain/outbox'
import { newId } from '@/lib/id'
import {
  type AttendanceRecord,
  type AttendanceStatus,
  type LocalData,
  type Meeting,
  type Member,
  type PushRejectCode,
  type PushResult,
  applyPull,
  attendanceKey,
  cleanText,
  emptyData,
  normalizeReason,
} from '@cadoan/shared'
import { create } from 'zustand'

/**
 * The app's single store: who is signed in, the offline copy of the data, and
 * the state of sending it.
 *
 * Write path (rule: the UI never waits on the network): a mark updates memory
 * at once, is written to IndexedDB together with an outbox entry, and a send
 * is scheduled. The send round is push-then-pull; see `runSync`.
 */

export type AuthStatus = 'loading' | 'error' | 'signedOut' | 'signedIn'

export interface Rejection {
  id: string
  code: PushRejectCode
  kind: 'attendance' | 'meeting'
  memberName: string | null
  meetingDate: string | null
  at: number
}

export interface MeetingInput {
  id?: string
  date: string
  time: string | null
  note: string
}

/** Who was what before a bulk change, so "Hoàn tác" can put it back. */
export type MarkSnapshot = { memberId: string; status: AttendanceStatus; reason: string | null }[]

interface State {
  status: AuthStatus
  session: repo.StoredSession | null
  /** Why the last sign-out happened without the user asking, for the login screen. */
  signedOutReason: 'locked' | null
  data: LocalData
  outboxKeys: ReadonlySet<string>
  cursor: number
  online: boolean
  running: boolean
  lastSyncAt: number | null
  lastError: 'network' | 'server' | null
  needsReauth: boolean
  rejections: Rejection[]
  lastWriteError: repo.WriteFailure | null

  hydrate: () => Promise<void>
  login: (username: string, password: string) => Promise<void>
  reauth: (password: string) => Promise<void>
  logout: () => Promise<void>
  changePassword: (current: string, next: string) => Promise<void>
  mark: (
    meetingId: string,
    memberId: string,
    status: AttendanceStatus,
    reason?: string | null,
  ) => void
  markMany: (meetingId: string, memberIds: string[], status: AttendanceStatus) => MarkSnapshot
  restore: (meetingId: string, snapshot: MarkSnapshot) => void
  saveMeeting: (input: MeetingInput) => string
  /**
   * Add (no id) or edit a member. Online only, like the console: the list is
   * shared by every phone and changes rarely, so it is not worth a merge rule.
   * Throws the ApiFailure for the form to show; auth failures also take their
   * usual path (re-login, lock).
   */
  saveMember: (id: string | null, patch: MemberPatch) => Promise<Member>
  syncNow: () => Promise<void>
  setOnline: (online: boolean) => void
  dismissRejections: () => void
  dismissWriteError: () => void
}

const initialSyncState = {
  data: emptyData(),
  outboxKeys: new Set<string>(),
  cursor: 0,
  running: false,
  lastSyncAt: null,
  lastError: null,
  needsReauth: false,
  rejections: [],
}

/** Seam for tests: the API the store calls. */
let api: Api = realApi
export function setApiForTests(next: Api): void {
  api = next
}

/**
 * Stamps strictly increase on this phone, even for two taps in the same
 * millisecond, so the later tap always wins its own conflict.
 */
let lastStamp = 0
function nextStamp(): number {
  lastStamp = Math.max(Date.now(), lastStamp + 1)
  return lastStamp
}

const SEND_DELAY_MS = 800
let sendTimer: ReturnType<typeof setTimeout> | null = null
let inflight: Promise<void> | null = null
let again = false

export const useAppStore = create<State>()((set, get) => {
  repo.onWriteFailure((failure) => set({ lastWriteError: failure }))

  function scheduleSync(): void {
    if (sendTimer) clearTimeout(sendTimer)
    sendTimer = setTimeout(() => {
      sendTimer = null
      void get().syncNow()
    }, SEND_DELAY_MS)
  }

  function me() {
    const user = get().session?.user
    if (!user) throw new Error('No signed-in user')
    return user
  }

  function putMarks(records: AttendanceRecord[]): void {
    const s = get()
    const attendance = new Map(s.data.attendance)
    const keys = new Set(s.outboxKeys)
    const entries: OutboxEntry[] = records.map((r) => {
      attendance.set(attendanceKey(r.meetingId, r.memberId), r)
      const entry = toEntry({
        kind: 'attendance',
        meetingId: r.meetingId,
        memberId: r.memberId,
        status: r.status,
        reason: r.reason,
        at: r.markedAt,
      })
      keys.add(entry.key)
      return entry
    })
    set({ data: { ...s.data, attendance }, outboxKeys: keys })
    if (records.length === 1 && records[0] && entries[0]) repo.saveMark(records[0], entries[0])
    else repo.saveMarks(records, entries)
    scheduleSync()
  }

  function newMark(
    meetingId: string,
    memberId: string,
    status: AttendanceStatus,
    reason: string | null,
  ): AttendanceRecord {
    return {
      meetingId,
      memberId,
      status,
      reason: normalizeReason(status, reason),
      markedBy: me().id,
      markedAt: nextStamp(),
      rev: 0,
    }
  }

  async function signOutLocally(reason: 'locked' | null): Promise<void> {
    if (sendTimer) clearTimeout(sendTimer)
    await repo.clearAll()
    set({
      ...initialSyncState,
      data: emptyData(),
      outboxKeys: new Set(),
      session: null,
      status: 'signedOut',
      signedOutReason: reason,
    })
  }

  /** Every API failure outside the login form lands here. */
  async function handleFailure(err: unknown): Promise<void> {
    if (!(err instanceof ApiFailure)) {
      console.error(err)
      set({ lastError: 'server' })
      return
    }
    if (err.kind === 'network') return set({ lastError: 'network' })
    if (err.kind === 'server') return set({ lastError: 'server' })
    switch (err.code) {
      case 'account_locked':
        // A locked account loses its queued changes: that is what locking is for.
        await signOutLocally('locked')
        return
      case 'unauthorized':
      case 'session_expired':
        // Keep everything; the phone keeps working and sends after a re-login.
        set({ needsReauth: true, lastError: null })
        return
      case 'password_change_required': {
        const s = get().session
        if (s) {
          const session = { ...s, user: { ...s.user, mustChangePassword: true } }
          repo.saveSession(session)
          set({ session })
        }
        return
      }
      default:
        set({ lastError: 'server' })
    }
  }

  async function settlePush(sent: OutboxEntry[], results: PushResult[]): Promise<void> {
    await repo.removeSent(sent)
    const stillQueued = new Set((await repo.outboxAll()).map((e) => e.key))
    const s = get()
    const rejections: Rejection[] = []
    let { attendance, meetings } = s.data
    const dropKeys: string[] = []

    sent.forEach((entry, i) => {
      const r = results[i]
      if (!r || r.ok) return
      const { op } = entry
      if (op.kind === 'attendance') {
        const key = attendanceKey(op.meetingId, op.memberId)
        // A newer local write for the same record is still queued: keep it.
        if (!stillQueued.has(entry.key)) dropKeys.push(key)
        rejections.push({
          id: newId('rej'),
          code: r.code,
          kind: 'attendance',
          memberName: s.data.members.get(op.memberId)?.name ?? null,
          meetingDate: s.data.meetings.get(op.meetingId)?.date ?? null,
          at: Date.now(),
        })
      } else {
        if (r.code === 'meeting_deleted') {
          meetings = new Map(meetings)
          meetings.delete(op.id)
          repo.deleteLocalMeeting(op.id)
          attendance = new Map([...attendance].filter(([, a]) => a.meetingId !== op.id))
        }
        rejections.push({
          id: newId('rej'),
          code: r.code,
          kind: 'meeting',
          memberName: null,
          meetingDate: op.date,
          at: Date.now(),
        })
      }
    })

    if (dropKeys.length) {
      attendance = new Map(attendance)
      for (const k of dropKeys) attendance.delete(k)
      repo.deleteLocalAttendance(dropKeys)
    }
    set({
      data: { ...get().data, attendance, meetings },
      outboxKeys: stillQueued,
      rejections: rejections.length ? [...get().rejections, ...rejections] : get().rejections,
    })
  }

  /**
   * One send round: push everything queued, then pull what changed. Push first
   * so the pull already reflects this phone's own writes.
   */
  async function runSync(): Promise<void> {
    const s = get()
    const session = s.session
    if (s.status !== 'signedIn' || !session || s.needsReauth || session.user.mustChangePassword)
      return
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      set({ online: false })
      return
    }
    set({ running: true })
    try {
      await repo.flushWrites()
      const queued = orderForPush(await repo.outboxAll())
      for (let i = 0; i < queued.length; i += PUSH_CHUNK) {
        const chunk = queued.slice(i, i + PUSH_CHUNK)
        const res = await api.push(
          session.token,
          chunk.map((e) => e.op),
        )
        await settlePush(chunk, res.results)
      }

      const pull = await api.pull(session.token, get().cursor)
      await repo.flushWrites()
      const outbox = await repo.outboxAll()
      const { data, changes } = applyPull(get().data, pull, pendingKeys(outbox))
      repo.applyChanges(changes)
      repo.saveCursor(pull.rev)
      const now = Date.now()
      repo.saveLastSyncAt(now)
      set({
        data,
        cursor: pull.rev,
        lastSyncAt: now,
        lastError: null,
        online: true,
        outboxKeys: new Set(outbox.map((e) => e.key)),
      })
    } catch (err) {
      await handleFailure(err)
    } finally {
      set({ running: false })
    }
  }

  return {
    status: 'loading',
    session: null,
    signedOutReason: null,
    online: typeof navigator === 'undefined' ? true : navigator.onLine !== false,
    lastWriteError: null,
    ...initialSyncState,

    async hydrate() {
      set({ status: 'loading' })
      let loaded: repo.Loaded
      try {
        loaded = await repo.loadAll()
      } catch (err) {
        console.error(err)
        set({ status: 'error' })
        return
      }
      if (!loaded.session) {
        set({ status: 'signedOut' })
        return
      }
      set({
        status: 'signedIn',
        session: loaded.session,
        data: loaded.data,
        outboxKeys: new Set(loaded.outbox.map((e) => e.key)),
        cursor: loaded.cursor,
        lastSyncAt: loaded.lastSyncAt,
      })
      // Refresh the account in the background: the app is already usable
      // offline, and a lock or role change shows up as soon as we are online.
      const token = loaded.session.token
      api.me(token).then(
        (user) => {
          const current = get().session
          if (!current || current.token !== token) return
          const session = { ...current, user }
          repo.saveSession(session)
          set({ session })
        },
        (err) => void handleFailure(err),
      )
    },

    async login(username, password) {
      const res = await api.login(username, password)
      const previous = get().session
      // Another account's offline data never mixes with this one.
      if (!previous || previous.user.id !== res.user.id) {
        await repo.clearAll()
        set({ ...initialSyncState, data: emptyData(), outboxKeys: new Set() })
      }
      const session = { token: res.token, user: res.user }
      repo.saveSession(session)
      set({ session, status: 'signedIn', needsReauth: false, signedOutReason: null })
      void get().syncNow()
    },

    async reauth(password) {
      const current = get().session
      if (!current) return
      const res = await api.login(current.user.username, password)
      const session = { token: res.token, user: res.user }
      repo.saveSession(session)
      set({ session, needsReauth: false })
      void get().syncNow()
    },

    async logout() {
      const token = get().session?.token
      if (token) api.logout(token).catch(() => {})
      await signOutLocally(null)
    },

    async changePassword(current, next) {
      const session = get().session
      if (!session) return
      await api.changePassword(session.token, { currentPassword: current, newPassword: next })
      const updated = { ...session, user: { ...session.user, mustChangePassword: false } }
      repo.saveSession(updated)
      set({ session: updated })
      void get().syncNow()
    },

    mark(meetingId, memberId, status, reason = null) {
      putMarks([newMark(meetingId, memberId, status, reason)])
    },

    markMany(meetingId, memberIds, status) {
      const { attendance } = get().data
      const snapshot: MarkSnapshot = memberIds.map((memberId) => {
        const prev = attendance.get(attendanceKey(meetingId, memberId))
        return { memberId, status: prev?.status ?? 'unmarked', reason: prev?.reason ?? null }
      })
      putMarks(memberIds.map((id) => newMark(meetingId, id, status, null)))
      return snapshot
    },

    restore(meetingId, snapshot) {
      // Undo is a new write with a new stamp: the old one may already be on the
      // server, and only a later stamp can beat it there.
      putMarks(snapshot.map((x) => newMark(meetingId, x.memberId, x.status, x.reason)))
    },

    async saveMember(id, patch) {
      const session = get().session
      if (!session) throw new Error('No signed-in user')
      let member: Member
      try {
        member = id
          ? await api.updateMember(session.token, id, patch)
          : await api.createMember(session.token, {
              name: patch.name ?? '',
              joinedOn: patch.joinedOn ?? '',
            })
      } catch (err) {
        if (
          err instanceof ApiFailure &&
          err.kind === 'api' &&
          err.code !== 'invalid' &&
          err.code !== 'not_found'
        )
          await handleFailure(err)
        throw err
      }
      const s = get()
      const members = new Map(s.data.members)
      members.set(member.id, member)
      set({ data: { ...s.data, members } })
      repo.saveMember(member)
      return member
    },

    saveMeeting(input) {
      const user = me()
      const s = get()
      const id = input.id ?? newId('mt')
      const existing = s.data.meetings.get(id)
      const at = nextStamp()
      const meeting: Meeting = {
        id,
        date: input.date,
        time: input.time || null,
        note: cleanText(input.note),
        createdBy: existing?.createdBy ?? user.id,
        updatedAt: at,
        updatedBy: user.id,
        deleted: false,
        rev: existing?.rev ?? 0,
      }
      const entry = toEntry({
        kind: 'meeting',
        id,
        date: meeting.date,
        time: meeting.time,
        note: meeting.note,
        at,
      })
      const meetings = new Map(s.data.meetings)
      meetings.set(id, meeting)
      set({ data: { ...s.data, meetings }, outboxKeys: new Set(s.outboxKeys).add(entry.key) })
      repo.saveMeeting(meeting, entry)
      scheduleSync()
      return id
    },

    syncNow() {
      if (inflight) {
        again = true
        return inflight
      }
      inflight = runSync().finally(() => {
        inflight = null
        if (again) {
          again = false
          void get().syncNow()
        }
      })
      return inflight
    },

    setOnline(online) {
      set({ online, ...(online ? {} : { lastError: null }) })
      if (online) void get().syncNow()
    },

    dismissRejections: () => set({ rejections: [] }),
    dismissWriteError: () => set({ lastWriteError: null }),
  }
})

/** Test-only: forget the module-level timers between tests. */
export function resetStoreTimersForTests(): void {
  if (sendTimer) clearTimeout(sendTimer)
  sendTimer = null
  inflight = null
  again = false
  lastStamp = 0
}

/** Test-only: resolve once no send round is running or chained. */
export async function settleSyncForTests(): Promise<void> {
  while (inflight) await inflight
}
